import React, { useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import Loader from "../../Loader/Loader";

const EditEmailDialog = ({ currentEmail, userId, onEmailUpdated }) => {
  const [newEmail, setNewEmail] = useState(currentEmail || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !validateEmail(newEmail)) {
      setMessage("Por favor ingresa un email válido.");
      return;
    }

    if (newEmail === currentEmail) {
      setMessage("El nuevo email debe ser diferente al actual.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // Obtener el dominio del entorno (puedes ajustar esto según tu configuración)
      const DOMINIO = process.env.REACT_APP_API_URL; // Ajusta según tu configuración
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${DOMINIO}/admin/usuario/${userId}/email`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          // Agregar headers de autenticación si es necesario
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          newEmail: newEmail,
        }),
      });      if (!response.ok) {
        // Intentar parsear como JSON, si falla usar el status como mensaje
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
        } catch {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Leer la respuesta como texto primero
      const responseText = await response.text();
      
      // Intentar parsear como JSON, si falla usar el texto directamente
      let successMessage;
      try {
        const responseData = JSON.parse(responseText);
        successMessage = responseData.message || "Email actualizado exitosamente.";
      } catch {
        // Si no es JSON válido, usar el texto directamente
        successMessage = responseText || "Email actualizado exitosamente.";
      }

      setMessage(successMessage);
      
      // Llamar al callback para notificar al componente padre
      if (onEmailUpdated) {
        onEmailUpdated(newEmail);
      }

      // Cerrar el diálogo después de un breve delay
      setTimeout(() => {
        setIsOpen(false);
        setMessage("");
      }, 2000);

    } catch (error) {
      console.error("Error updating email:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setNewEmail(currentEmail || "");
    setMessage("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="ml-2 px-3 py-1 text-sm bg-blue-50 hover:bg-blue-100 border-blue-300"
        >
          Cambiar Email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        {loading && <Loader />}
        <DialogHeader>
          <DialogTitle>Cambiar Email del Usuario</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Actual
            </label>
            <input
              type="email"
              value={currentEmail || ""}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
              disabled
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nuevo Email
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Ingresa el nuevo email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {message && (
            <div className={`p-3 rounded-md text-sm ${
              message.includes("Error") || message.includes("válido") || message.includes("diferente")
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-green-50 text-green-700 border border-green-200"
            }`}>
              {message}
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateEmail}
            disabled={loading || !newEmail}
            className="bg-blue-500 hover:bg-blue-600"
          >
            {loading ? "Actualizando..." : "Actualizar Email"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditEmailDialog;
